import React, { useState } from 'react';
import { useSession, FleetShip } from 'session-context';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import VesselSelector from './vessel-selector';
import InteractiveShipHull from './interactive-ship-hull';
import ShipStatusTracker from './ship-status-tracker';
import { Ship, Plus, Trash2, AlertCircle } from 'lucide-react';
import { VESSEL_DATA } from './types/vessel';
import { VesselSection } from './types/vessel';

type DeckType = 'upper' | 'lower' | `middle${number}`;

const DMFleetManager: React.FC = () => {
  const { gameState, addFleetShip, updateFleetShip, removeFleetShip, isDM } = useSession();
  const [selectedShipId, setSelectedShipId] = useState<string | null>(null);
  const [newShipName, setNewShipName] = useState('');
  const [newShipVessel, setNewShipVessel] = useState('Caravel');
  const [showAddForm, setShowAddForm] = useState(false);

  if (!isDM()) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-amber-500" />
            DM Access Required
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Fleet management is only available to the Dungeon Master.
          </p>
        </CardContent>
      </Card>
    );
  }

  const fleet = gameState?.dmFleet || [];
  const selectedShip = fleet.find(s => s.id === selectedShipId);

  const handleAddShip = async () => {
    if (!newShipName.trim()) return;
    await addFleetShip(newShipName, newShipVessel);
    setNewShipName('');
    setShowAddForm(false);
  };

  const handleRemoveShip = async (id: string) => {
    await removeFleetShip(id);
    if (selectedShipId === id) {
      setSelectedShipId(null);
    }
  };

  const handleDeckUpdate = (deckType: DeckType, sections: VesselSection[]) => {
    if (!selectedShip) return;

    const updatedShip: FleetShip = {
      ...selectedShip,
      deckSections: {
        ...selectedShip.deckSections,
        [deckType]: sections.map(section => ({ ...section }))
      }
    };

    updateFleetShip(selectedShip.id, updatedShip);
  };

  const handleAllDecksUpdate = (deckSections: FleetShip['deckSections']) => {
    if (!selectedShip) return;

    const updatedShip: FleetShip = {
      ...selectedShip,
      deckSections
    };

    updateFleetShip(selectedShip.id, updatedShip);
  };

  const handleRiggingUpdate = (sections: VesselSection[]) => {
    if (!selectedShip) return;

    const updatedShip: FleetShip = {
      ...selectedShip,
      riggingSections: sections
    };

    updateFleetShip(selectedShip.id, updatedShip);
  };

  const handleRepairAll = (
    deckSections: FleetShip['deckSections'],
    riggingSections: VesselSection[]
  ) => {
    if (!selectedShip) return;

    const updatedShip: FleetShip = {
      ...selectedShip,
      deckSections,
      riggingSections
    };

    updateFleetShip(selectedShip.id, updatedShip);
  };

  const calculateHealthPercentage = (ship: FleetShip): number => {
    const allSections = [
      ...(ship.deckSections?.upper || []),
      ...Object.entries(ship.deckSections || {})
        .filter(([key]) => key.startsWith('middle'))
        .flatMap(([_, sections]) => sections as VesselSection[]),
      ...(ship.deckSections?.lower || []),
      ...(ship.riggingSections || [])
    ];

    const totalCurrent = allSections.reduce((sum, s) => sum + s.current, 0);
    const totalMax = allSections.reduce((sum, s) => sum + s.max, 0);

    return totalMax > 0 ? Math.round((totalCurrent / totalMax) * 100) : 0;
  };

  const getHealthColor = (percentage: number): string => {
    if (percentage <= 0) return 'text-red-600';
    if (percentage <= 50) return 'text-yellow-600';
    return 'text-green-600';
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Fleet Management</h2>
        <Button onClick={() => setShowAddForm(!showAddForm)} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Add Ship
        </Button>
      </div>

      {showAddForm && (
        <Card>
          <CardHeader>
            <CardTitle>Add New Ship to Fleet</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium">Ship Name</label>
              <Input
                value={newShipName}
                onChange={(e) => setNewShipName(e.target.value)}
                placeholder="Enter ship name"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Vessel Type</label>
              <VesselSelector
                onVesselSelect={setNewShipVessel}
                selectedVessel={newShipVessel}
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleAddShip}>Add Ship</Button>
              <Button variant="outline" onClick={() => setShowAddForm(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-12 gap-6">
        {/* Ship List Sidebar */}
        <div className="col-span-3 space-y-2">
          <h3 className="text-lg font-semibold">Fleet Ships</h3>
          {fleet.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                No ships in fleet. Add a ship to get started.
              </CardContent>
            </Card>
          ) : (
            fleet.map(ship => {
              const healthPct = calculateHealthPercentage(ship);
              return (
                <Card
                  key={ship.id}
                  className={`cursor-pointer transition-colors ${
                    selectedShipId === ship.id ? 'border-primary' : ''
                  }`}
                  onClick={() => setSelectedShipId(ship.id)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Ship className="h-4 w-4" />
                          <h4 className="font-semibold">{ship.name}</h4>
                        </div>
                        <p className="text-sm text-muted-foreground">{ship.vesselType}</p>
                        <p className={`text-sm font-medium ${getHealthColor(healthPct)}`}>
                          {healthPct}% Health
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveShip(ship.id);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>

        {/* Selected Ship View */}
        <div className="col-span-9">
          {selectedShip ? (
            <div className="grid grid-cols-12 gap-6">
              <div className="col-span-8">
                <InteractiveShipHull
                  upperDeckImage="https://i.imgur.com/wxTzrKd.jpeg"
                  bottomDeckImage="https://i.imgur.com/L0c4qpV.jpeg"
                  selectedVessel={selectedShip.vesselType}
                  deckSections={selectedShip.deckSections}
                  riggingSections={selectedShip.riggingSections}
                  onDeckUpdate={handleDeckUpdate}
                  onRiggingUpdate={handleRiggingUpdate}
                  onAllDecksUpdate={handleAllDecksUpdate}
                  onRepairAll={handleRepairAll}
                />
              </div>
              <div className="col-span-4">
                <ShipStatusTracker
                  upperDeckSections={selectedShip.deckSections?.upper || []}
                  bottomDeckSections={selectedShip.deckSections?.lower || []}
                  riggingSections={selectedShip.riggingSections || []}
                />
              </div>
            </div>
          ) : (
            <Card>
              <CardContent className="py-16 text-center text-muted-foreground">
                Select a ship from the fleet to view and manage its status
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default DMFleetManager;
